import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Globe, ShieldCheck, Loader2 } from "lucide-react";
import { MaskedLine, Reveal, Overline } from "@/components/motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CONTACT, INQUIRY_TYPES } from "@/data/content";

const inputCls =
  "bg-[#12151E] border-[#282E3F] text-slate-100 placeholder:text-slate-500 focus-visible:ring-sky-500/60 focus-visible:border-sky-500/50 h-12";

const selectCls =
  "w-full h-12 rounded-md border border-[#282E3F] bg-[#12151E] px-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-500/60 focus:border-sky-500/50 transition-colors";

const INITIAL = { name: "", email: "", phone: "", division: "general", inquiry_type: "general", message: "" };

const Contact = () => {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/enquiries", form);
      toast.success(data.message);
      setForm(INITIAL);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative pt-44 pb-20 overflow-hidden hero-glow grain">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <MaskedLine delay={0.15}>
            <span className="font-mono text-xs uppercase tracking-[0.35em] text-sky-400/90">Institutional Desk</span>
          </MaskedLine>
          <h1 className="mt-6 font-display font-semibold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl" data-testid="contact-title">
            <MaskedLine delay={0.3}>Head Office &</MaskedLine>
            <MaskedLine delay={0.45}>
              <span className="gold-text">Operations Enquiry</span>
            </MaskedLine>
          </h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-12 pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <div className="card-institutional h-full">
            <Overline>Registered Office</Overline>
            <h2 className="mt-5 font-display font-semibold tracking-tight text-xl sm:text-2xl">{CONTACT.headOffice}</h2>
            <div className="mt-8 space-y-6 text-sm">
              <div className="flex gap-4">
                <MapPin size={18} className="text-sky-400/90 shrink-0 mt-0.5" />
                <p className="text-slate-300 leading-relaxed" data-testid="contact-address">{CONTACT.address}</p>
              </div>
              <div className="flex gap-4">
                <Globe size={18} className="text-sky-400/90 shrink-0 mt-0.5" />
                <p className="text-slate-300">{CONTACT.operations}</p>
              </div>
              <div className="flex gap-4">
                <ShieldCheck size={18} className="text-sky-400/90 shrink-0 mt-0.5" />
                <p className="text-slate-400 leading-relaxed">
                  All enquiries are reviewed by the institutional desk and routed to the relevant division within one working day.
                </p>
              </div>
            </div>
            <div className="mt-10 pt-8 border-t border-[#282E3F] font-mono text-[10px] tracking-[0.25em] text-slate-500 space-y-2">
              <p>DIV 01 — STRATEGIC INFRASTRUCTURE & REAL ESTATE</p>
              <p>DIV 02 — ADVANCED INTELLIGENCE & DIGITAL SYSTEMS</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-7">
          <form onSubmit={submit} className="card-institutional" data-testid="enquiry-form">
            <Overline>Enquiry Form</Overline>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name" className="text-slate-300 text-xs tracking-wide">Full Name / Entity Name</Label>
                <Input id="name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} placeholder="e.g. Meridian Holdings LLP" className={`${inputCls} mt-2`} />
              </div>
              <div>
                <Label htmlFor="email" className="text-slate-300 text-xs tracking-wide">Official Email</Label>
                <Input id="email" type="email" data-testid="enquiry-email-input" required value={form.email} onChange={set("email")} placeholder="name@entity.com" className={`${inputCls} mt-2`} />
              </div>
              <div>
                <Label htmlFor="phone" className="text-slate-300 text-xs tracking-wide">Contact Phone</Label>
                <Input id="phone" data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={`${inputCls} mt-2`} />
              </div>
              <div>
                <Label htmlFor="division" className="text-slate-300 text-xs tracking-wide">Department / Division</Label>
                <select id="division" data-testid="enquiry-division-select" value={form.division} onChange={set("division")} className={`${selectCls} mt-2`}>
                  <option value="general">General</option>
                  <option value="infrastructure">Strategic Infrastructure & Real Estate</option>
                  <option value="technology">Advanced Intelligence & Digital Systems</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="inquiry_type" className="text-slate-300 text-xs tracking-wide">Inquiry Type</Label>
                <select id="inquiry_type" data-testid="enquiry-type-select" value={form.inquiry_type} onChange={set("inquiry_type")} className={`${selectCls} mt-2`}>
                  {INQUIRY_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="message" className="text-slate-300 text-xs tracking-wide">Project / Maintenance Scope Brief</Label>
                <Textarea
                  id="message"
                  data-testid="enquiry-message-input"
                  required
                  minLength={10}
                  rows={6}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Outline the mandate, asset class, or systems environment…"
                  className="bg-[#12151E] border-[#282E3F] text-slate-100 placeholder:text-slate-500 focus-visible:ring-sky-500/60 focus-visible:border-sky-500/50 mt-2"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="enquiry-submit-btn"
              className="mt-8 inline-flex items-center gap-2 bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] text-white font-semibold text-sm px-8 py-4 rounded-full hover:shadow-[0_0_32px_rgba(59,130,246,0.45)] transition-shadow duration-300 disabled:opacity-60"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {submitting ? "Transmitting…" : "Submit Institutional Enquiry"}
            </button>
          </form>
        </Reveal>
      </section>
    </div>
  );
};

export default Contact;
